import dayjs from 'dayjs';
import { mutate as globalMutate } from 'swr';
import { ROUTES } from '../../../../constants/routes';
import { apiUrls } from '../../../../utils/apiUtils';
import { TransactionType } from '../components/types';
import { getTransactionUrl } from '../helpers';
import { TransactionFormState } from '../hooks/useTransactionFormState';

/**
 * Marker written onto an optimistically-seeded detail DTO. While present, the
 * detail screen is showing data reconstructed from the create form (not the
 * real GET response), so the edit action must stay disabled — the seed is
 * render-complete but NOT edit-complete (it lacks some ids/typeCodes the edit
 * form relies on). The `{ revalidate: true }` on the seed triggers the real
 * GET immediately; when it resolves, SWR replaces the cache value with the
 * server DTO (which has no marker) and edit re-enables automatically.
 */
export const OPTIMISTIC_DETAIL_FLAG = '__optimistic';

/** True when a detail DTO is still the optimistic seed (real GET not yet reconciled). */
export const isOptimisticDetail = (obj: unknown): boolean =>
  !!obj && (obj as Record<string, unknown>)[OPTIMISTIC_DETAIL_FLAG] === true;

interface SeedResult {
  key: string;
  value: { data: Record<string, unknown> };
}

/** Project a form-state category onto the fields the detail renderers read. */
const toCategory = (c: {
  id?: string;
  name?: string;
  icon?: string;
  iconColor?: string;
  backgroundColor?: string;
} | null | undefined) =>
  c
    ? {
        id: c.id,
        name: c.name,
        icon: c.icon,
        iconColor: c.iconColor,
        backgroundColor: c.backgroundColor,
      }
    : undefined;

/**
 * Build an optimistic detail-cache seed from the create form state plus the id
 * returned by the create POST. The target SWR key and DTO shape are chosen from
 * the SAME route the submit handler navigates to (via getTransactionUrl), so the
 * seed always lands on the exact page that will render. Returns null when there
 * is nothing safe to seed.
 */
export const buildOptimisticDetailSeed = (
  formState: TransactionFormState,
  createdId: string
): SeedResult | null => {
  if (!createdId) return null;

  const {
    currentTab,
    loanType,
    amount,
    description,
    datePicker,
    isExcludedFromReport,
    expenseWallet,
    incomeWallet,
    transferSourceWallet,
    selectDestinationWallet,
    spendingCategory,
    incomeCategory,
    loanWallet,
    repaymentWallet,
    sourceWallet,
    lendWallets,
  } = formState;

  const route = getTransactionUrl(currentTab, loanType, expenseWallet?.wallet);

  const common = {
    amount: Math.abs(amount || 0),
    description: description || '',
    transactionDate: dayjs(datePicker).toISOString(),
    isExcludedFromReport: isExcludedFromReport || false,
    [OPTIMISTIC_DETAIL_FLAG]: true,
  };

  switch (route) {
    case ROUTES.BORROWING_TRANSACTION_DETAIL: {
      // Reached either from the Loan tab (borrow / borrow-expense) or from the
      // Expense tab when the paying wallet is a loan wallet (borrow-expense).
      const isBorrowExpense =
        loanType === 'borrow-expense' ||
        (currentTab === TransactionType.EXPENSE &&
          expenseWallet?.wallet?.type === 'loan');

      if (isBorrowExpense) {
        return {
          key: apiUrls.borrowingTransactionDetailById(createdId),
          value: {
            data: {
              ...common,
              category: toCategory(spendingCategory),
              // For borrow-expense the lender is stored in expenseWallet.
              loanWallet: { name: expenseWallet?.wallet?.name || '' },
              wallet: { name: '' },
              transaction: {
                id: createdId,
                type: 'borrow-expense',
                category: toCategory(spendingCategory),
              },
            },
          },
        };
      }

      // Regular borrow: loanWallet = lender, repaymentWallet = receiving wallet.
      return {
        key: apiUrls.borrowingTransactionDetailById(createdId),
        value: {
          data: {
            ...common,
            category: undefined,
            loanWallet: { name: loanWallet?.wallet?.name || '' },
            wallet: { name: repaymentWallet?.wallet?.name || '' },
            transaction: { id: createdId, type: 'borrow' },
          },
        },
      };
    }

    case ROUTES.LENDING_TRANSACTION_DETAIL: {
      const personName =
        lendWallets && lendWallets.length > 0
          ? lendWallets[0]?.wallet?.wallet?.name || ''
          : '';
      return {
        key: apiUrls.lendingTransactionDetailById(createdId),
        value: {
          data: {
            ...common,
            category: undefined,
            wallet: { name: sourceWallet?.wallet?.name || '' },
            loans: [{ id: `optimistic-${createdId}`, wallet: { name: personName } }],
          },
        },
      };
    }

    case ROUTES.REPAYMENT_TRANSACTION_DETAIL:
      return {
        key: apiUrls.repaymentTransactionDetailById(createdId),
        value: {
          data: {
            ...common,
            category: undefined,
            loanWallet: { name: loanWallet?.wallet?.name || '' },
            wallet: { name: repaymentWallet?.wallet?.name || '' },
            repayments: [],
          },
        },
      };

    case ROUTES.COLLECTING_TRANSACTION_DETAIL:
      return {
        key: apiUrls.collectingTransactionDetailById(createdId),
        value: {
          data: {
            ...common,
            category: undefined,
            loanWallet: { name: loanWallet?.wallet?.name || '' },
            wallet: { name: repaymentWallet?.wallet?.name || '' },
            collectings: [],
          },
        },
      };

    case ROUTES.TRANSACTION_DETAIL:
    default: {
      // Base detail: expense / income / transfer / credit-card (pay-balance).
      let type = 'expense';
      let walletName = '';
      let walletId: string | undefined;
      let category: ReturnType<typeof toCategory>;
      let categoryId: string | undefined;
      let transfer:
        | { sourceWallet: string; destinationWallet: string }
        | undefined;

      if (currentTab === TransactionType.INCOME) {
        type = 'income';
        walletName = incomeWallet?.wallet?.name || '';
        walletId = incomeWallet?.wallet?.id;
        category = toCategory(incomeCategory);
        categoryId = incomeCategory?.id;
      } else if (
        currentTab === TransactionType.TRANSFER ||
        currentTab === TransactionType.CREDIT_CARD
      ) {
        type = 'transfer';
        transfer = {
          sourceWallet: transferSourceWallet?.wallet?.name || '',
          destinationWallet: selectDestinationWallet?.wallet?.name || '',
        };
      } else {
        type = 'expense';
        walletName = expenseWallet?.wallet?.name || '';
        walletId = expenseWallet?.wallet?.id;
        category = toCategory(spendingCategory);
        categoryId = spendingCategory?.id;
      }

      return {
        key: apiUrls.transactionById(createdId),
        value: {
          data: {
            ...common,
            id: createdId,
            type,
            wallet: walletName,
            walletId,
            category,
            categoryId,
            transfer,
          },
        },
      };
    }
  }
};

/**
 * Seed the transaction-detail SWR cache from the create form state so the
 * detail screen renders instantly, then let SWR revalidate to the real DTO.
 * Call this right after the create POST resolves, before navigating.
 */
export const seedOptimisticDetail = (
  formState: TransactionFormState,
  createdId: string
): void => {
  const seed = buildOptimisticDetailSeed(formState, createdId);
  if (!seed) return;
  globalMutate(seed.key, seed.value, { revalidate: true });
};
